import type { DeviceMapLocationPoint } from '../types/LiveMap.types';
import { LIVE_MAP_DEVICE_LOCATION_HISTORY_PAGE_LIMIT } from './constants';
import type { TimestampFilterInput } from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';
import type { RobotCleaningControlService } from 'app/modules/machine-inventory/RobotCleaningControlService';

const byUpdatedAtAscending = (a: DeviceMapLocationPoint, b: DeviceMapLocationPoint): number =>
  Date.parse(a.updatedAt) - Date.parse(b.updatedAt);

/**
 * Fetches the full `deviceMapLocation` history for the current cleaning run, one page at a time, oldest first.
 * Looked up by material and serial number: the machine id isn't the PRA device id and gives "Device not found".
 * The API pages newest-first and `lastTimestamp` returns points older than it.
 */
export const getDeviceMapLocationHistory = async (
  robotCleaningControlService: RobotCleaningControlService,
  materialNumber: string,
  serialNumber: string
): Promise<DeviceMapLocationPoint[]> => {
  const historyByTimestamp = new Map<string, DeviceMapLocationPoint>();
  let filter: TimestampFilterInput = {
    limit: LIVE_MAP_DEVICE_LOCATION_HISTORY_PAGE_LIMIT,
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // Each page's filter depends on the previous page's cursor — sequential, not parallelizable.
    // eslint-disable-next-line no-await-in-loop
    const data = await robotCleaningControlService.getDeviceMapLocationHistory({
      materialNumber,
      serialNumber,
      filter,
    });
    const page = data?.deviceMapLocation;

    if (!page || page.history.length === 0) {
      break;
    }

    const sizeBefore = historyByTimestamp.size;
    page.history.forEach(point => historyByTimestamp.set(point.updatedAt, point));

    // pageInfo.lastUpdatedAt is the newest point's time, so the cursor is the page's oldest point instead.
    const oldestPoint = [...page.history].sort(byUpdatedAtAscending)[0];

    if (historyByTimestamp.size === sizeBefore || historyByTimestamp.size >= page.pageInfo.totalCount) {
      break;
    }

    filter = { lastTimestamp: oldestPoint.updatedAt, limit: LIVE_MAP_DEVICE_LOCATION_HISTORY_PAGE_LIMIT };
  }

  return [...historyByTimestamp.values()].sort(byUpdatedAtAscending);
};
